import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { DataIntervalTagEditDialog } from '@app/shared/edit-dialog/data-interval-tag/data-interval-tag-edit-dialog.component';
import { DeviceEditDialog } from '@app/shared/edit-dialog/device/device-edit-dialog.component';
import { WebClient } from '@injectables';
import { Client, DataIntervalTag, Device, User, Site } from '@models';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { op } from '@lib/rxjs';
import { Subscription } from 'rxjs';
import { ModalFilter } from '@app/shared/model-table/model-table-filter';
import { ModelTableComponent } from '@app/shared/model-table/model-table.component';

@Component({
    selector: 'devices',
    templateUrl: './devices.component.html',
    styleUrls: ['./devices.component.scss'],
})
export class DevicesComponent implements OnInit, OnDestroy {
    constructor(
        private readonly dialog: DialogService,
        private readonly webClient: WebClient,
        private readonly fragQuery: FragQueryService,
        public readonly user: User
    ) { }
    @ViewChild(ModelTableComponent) 
    private deviceTable: ModelTableComponent<Device, WebClient.RestTypes>;
    private subscriptions: Subscription = new Subscription();

    public selected: string = 'name';
    public SearchText: string = '';
    public readonly particleSizeUnitSystem$ =
        this.fragQuery.particleSizeUnitSystem$;
    public readonly model = Device;
    public readonly restClient = this.webClient.model.device;
    public readonly editDialog = DeviceEditDialog;
    public readonly quantityUnitSystem$ = this.fragQuery.QuantityUnitSystem$;
    public readonly deletable = (v: Device) => {
        return this.user.permissions >= 4 && !v.uuid.startsWith('STRAYOS');
    };
    public readonly addable = (v?: Device) => {
        if (v) {
            return this.user.permissions >= 4 && !v.uuid.startsWith('STRAYOS');
        } else {
            return this.user.permissions >= 4;
        }
    };

    public readonly typeOptions = <Record<any, string>>Device.typeOptions;

    private clientOptions: { value: number; label: string }[] = [];
    public readonly clients$ = this.webClient.model.client.search$({}).pipe(
        op.map((values) => new Map(values.map((v) => [v.id, v]))),
        op.startWith(new Map<number, Client>())
    );
    public clientsUpdated$ = this.clients$.pipe(
        op.map((clientMap) => {
            const options = Array.from(clientMap.entries()).map(([id, value]) => ({
                value: id,
                label: value.name,
            }));
            return options;
        })
    );

    public readonly sites$ = this.webClient.model.site.search$({}).pipe(
        op.map((values) => new Map(values.map((s) => [s.id, s]))),
        op.startWith(new Map<number, Site>())
    );

    public ngOnInit(): void {
        this.subscriptions.add(this.fragQuery.userSettingUpdated$.subscribe(() => this.refresh()));
        this.subscriptions.add(this.clientsUpdated$.subscribe((clients) => {
            this.clientOptions = clients;
            this.addClientsInFilterConfig(this.clientOptions);
        }));
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public refresh() {
        this.deviceTable?.updateRecords();
    }

    private addClientsInFilterConfig(
        clientOptions: {
            value: number;
            label: string;
        }[]
    ) {
        if (clientOptions.length > 0) {
            this.filterConfigs.push({
                type: 'select-string',
                dbColumn: 'clientId',
                column: 'Client',
                isValueLabelPair:true,
                options: clientOptions,
            });
        }
    }

    public openNotes(record: Device) {
        this.dialog.message(record.notes);
    }

    public getEmails(emailCsv: string) {
        return emailCsv !== null ? emailCsv.split(',') : null;
    }

    public async setLiveTag(device: Device) {
        let tag = await this.webClient.model.dataIntervalTag.getLive(device.id);
        tag = tag?.clone() ?? new DataIntervalTag();
        tag.deviceId = device.id;
        tag.begin = null;
        tag.end = null;
        if (
            await this.dialog.open(DataIntervalTagEditDialog, {
                record: tag,
                showTimes: false,
            })
        ) {
            await this.webClient.model.dataIntervalTag.setLiveTag(tag);
        }
    }

    public filterConfigs: ModalFilter[] = [
        {
            column: 'Name',
            dbColumn: 'name',
            type: 'input-string',
        },
        {
            type: 'select-string',
            column: 'Type',
            isValueLabelPair: true,
            options: [
                { value: 'truck', label: 'Truck unload monitor' },
                { value: 'conveyor', label: 'Conveyor belt monitor' },
            ],
            dbColumn: 'type',
        },
        {
            column: 'Tolerance Alert',
            dbColumn: 'threshold',
            type: 'measurement-particleSize',
            unitSystem$: this.fragQuery.particleSizeUnitSystem$
        },
        {
            column: 'Slab Diameter',
            dbColumn: 'slabDiameter',
            type: 'measurement-particleSize',
            unitSystem$: this.fragQuery.particleSizeUnitSystem$
        },
        {
            column: 'Density',
            dbColumn: 'density',
            type: 'measurement-largeDensity',
            unitSystem$: this.fragQuery.QuantityUnitSystem$
        },
    ];
}
